import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Building, 
  Settings, 
  BarChart3, 
  Shield, 
  Menu,
  X,
  LogOut,
  Home
} from 'lucide-react';
import { authService } from '../api/authService';
import { useNotification } from '../contexts/NotificationContext';
import logoIcon from '../assets/img/Icon ASAlink Secondary.png';

export default function AdminDashboard({ children }) {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const { showError, showSuccess } = useNotification();

  useEffect(() => {
    console.log('=== AdminDashboard - Authentication Check ===');
    console.log('isAuthenticated:', authService.isAuthenticated());
    console.log('isAdmin:', authService.isAdmin());
    console.log('currentUser:', authService.getCurrentUser());
    console.log('token exists:', !!authService.getToken());
    
    // Check admin access
    if (!authService.isAuthenticated()) {
      console.log('AdminDashboard: User not authenticated, redirecting to login');
      showError('Access denied', 'Authentication required');
      navigate('/login');
      return;
    }

    if (!authService.isAdmin()) {
      console.log('AdminDashboard: User is not admin, redirecting to home');
      showError('Access denied', 'Admin privileges required');
      navigate('/');
      return;
    }

    // Get current user info
    const userData = authService.getCurrentUser();
    console.log('AdminDashboard: Admin access granted for user:', userData);
    setCurrentUser(userData);
  }, [navigate, showError]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      showSuccess('Berhasil logout');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      showError('Error during logout');
      navigate('/login', { replace: true });
    }
  };

  const menuItems = [
    {
      path: '/admin',
      label: 'Dashboard',
      icon: BarChart3,
      exact: true
    },
    {
      path: '/admin/users',
      label: 'User Management',
      icon: Users
    },
    {
      path: '/admin/tenants',
      label: 'Tenant Management',
      icon: Building
    },
    {
      path: '/admin/settings',
      label: 'System Settings',
      icon: Settings
    }
  ];

  const isActivePath = (path, exact = false) => {
    const currentPath = window.location.pathname;
    if (exact) {
      return currentPath === path;
    }
    return currentPath.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`bg-slate-800 text-white transition-all duration-300 relative ${isSidebarOpen ? 'w-64' : 'w-16'}`}>
        <div className="flex items-center gap-3 p-4 border-b border-slate-700">
          <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
            <img src={logoIcon} alt="ASAlink Logo" className="w-full h-full object-contain" />
          </div>
          {isSidebarOpen && (
            <div>
              <h1 className="font-bold text-lg">Admin Panel</h1>
              <p className="text-xs text-slate-400">POS Asalink</p>
            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="mt-6">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path, item.exact);
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white border-r-2 border-blue-400'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                {isSidebarOpen && <span>{item.label}</span>}
              </button>
            );
          })}

          {/* Divider */}
          <div className="border-t border-slate-700 my-4 mx-4" />

          {/* Back to Main App */}
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Home className="w-5 h-5" />
            {isSidebarOpen && <span>Back to Main App</span>}
          </button>
        </nav>

        {/* User Info & Logout */}
        {currentUser && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
            {isSidebarOpen ? (
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-medium text-white">{currentUser.full_name}</p>
                  <p className="text-xs text-slate-400">{currentUser.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 rounded transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={handleLogout}
                className="w-full p-2 text-slate-300 hover:bg-slate-700 rounded transition-colors flex justify-center"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            <div>
              <h2 className="font-semibold text-gray-800">Super Admin Dashboard</h2>
              <p className="text-sm text-gray-500">Manage users, tenants and system configuration</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
              System Administrator
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  );
}